import { type DialogProps } from '@mui/material/Dialog'
import React, { PropsWithChildren, ReactNode } from 'react'

import ConfirmationDialog from '../component/atom/confirmation-dialog'

type ConfirmationContentHelpers = {
  setConfirmButtonDisabled: (disabled: boolean) => void
}

type BaseOptions = {
  cancelButtonLabel?: string
  confirmButtonDisabled?: boolean
  confirmButtonLabel?: string
  content: ReactNode | ((helpers: ConfirmationContentHelpers) => ReactNode)
  fullWidth?: boolean
  hideCancel?: boolean
  icon?: string
  maxWidth?: DialogProps['maxWidth']
  title: ReactNode
}

type AdditionalAction =
  | {
      additionalButtonLabel: string
      onAdditionalClick: () => void
    }
  | { additionalButtonLabel?: never; onAdditionalClick?: never }

export type ConfirmationOptions = BaseOptions & AdditionalAction

type ConfirmationRequest = ConfirmationOptions & {
  id: number
  resolve: (confirmed: boolean) => void
}

type ContextValue = {
  createConfirmation: (options: ConfirmationOptions) => Promise<boolean>
}

type ConfirmationListener = (request: ConfirmationRequest) => void

const listeners = new Set<ConfirmationListener>()
const pendingRequests: ConfirmationRequest[] = []
let nextConfirmationRequestId = 0

const subscribeToConfirmations = (listener: ConfirmationListener) => {
  listeners.add(listener)

  if (pendingRequests.length) {
    const queued = [...pendingRequests]
    pendingRequests.length = 0
    queued.forEach(listener)
  }

  return () => {
    listeners.delete(listener)
  }
}

const emitConfirmation = (request: ConfirmationRequest) => {
  if (!listeners.size) {
    pendingRequests.push(request)
    return
  }

  listeners.forEach((listener) => listener(request))
}

export const createConfirmation = (options: ConfirmationOptions) =>
  new Promise<boolean>((resolve) => {
    emitConfirmation({ ...options, id: nextConfirmationRequestId++, resolve })
  })

let pendingNavigation: { callback: (confirmed: boolean) => void } | undefined

export const confirmNavigation = async (message: string, callback: (confirmed: boolean) => void) => {
  if (pendingNavigation) {
    // Repeated browser Back/Forward moves the URL before the dialog resolves.
    pendingNavigation.callback = callback
    return
  }
  const request = { callback }
  pendingNavigation = request
  let discard = false
  await createConfirmation({
    title: 'Unsaved changes',
    content: <span style={{ whiteSpace: 'pre-line' }}>{message}</span>,
    hideCancel: true,
    confirmButtonLabel: 'Return to editing',
    additionalButtonLabel: 'Discard Changes and Exit',
    onAdditionalClick: () => {
      discard = true
    },
  })
  pendingNavigation = undefined
  request.callback(discard)
}

const ConfirmationContext = React.createContext<ContextValue | undefined>(undefined)

export const ConfirmationProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [queue, setQueue] = React.useState<ConfirmationRequest[]>([])
  const activeRequest = queue[0]

  React.useEffect(() => subscribeToConfirmations((request) => setQueue((prev) => [...prev, request])), [])

  const resolveActive = React.useCallback((result: boolean) => {
    setQueue((prev) => {
      if (!prev.length) {
        return prev
      }

      const [current, ...rest] = prev
      current.resolve(result)

      return rest
    })
  }, [])

  const handleCancel = React.useCallback(() => resolveActive(false), [resolveActive])
  const handleConfirm = React.useCallback(() => resolveActive(true), [resolveActive])
  const additionalButtonProps = React.useMemo(() => {
    if (!activeRequest?.onAdditionalClick) {
      return {}
    }
    const { additionalButtonLabel, onAdditionalClick } = activeRequest
    return {
      additionalButtonText: additionalButtonLabel,
      onAdditionalClick: () => {
        onAdditionalClick()
        resolveActive(false)
      },
    }
  }, [activeRequest, resolveActive])
  const setConfirmButtonDisabled = React.useCallback((requestId: number, disabled: boolean) => {
    setQueue((prev) => {
      if (!prev.length || prev[0].id !== requestId || (prev[0].confirmButtonDisabled ?? false) === disabled) {
        return prev
      }

      const [current, ...rest] = prev

      return [{ ...current, confirmButtonDisabled: disabled }, ...rest]
    })
  }, [])
  const content = React.useMemo<ReactNode>(() => {
    if (!activeRequest) {
      return undefined
    }

    if (typeof activeRequest.content === 'function') {
      return activeRequest.content({
        setConfirmButtonDisabled: (disabled: boolean) => setConfirmButtonDisabled(activeRequest.id, disabled),
      })
    }

    return activeRequest.content
  }, [activeRequest, setConfirmButtonDisabled])

  return (
    <ConfirmationContext.Provider value={{ createConfirmation }}>
      {children}
      <ConfirmationDialog
        {...additionalButtonProps}
        id="global-confirmation-dialog"
        title={activeRequest?.title ?? ''}
        text={content ?? ''}
        isOpen={Boolean(activeRequest)}
        disabled={activeRequest?.confirmButtonDisabled ?? false}
        fullWidth={activeRequest?.fullWidth ?? false}
        hideCancel={activeRequest?.hideCancel ?? false}
        icon={activeRequest?.icon}
        maxWidth={activeRequest?.maxWidth}
        cancelButtonText={activeRequest?.cancelButtonLabel ?? 'Cancel'}
        deleteButtonText={activeRequest?.confirmButtonLabel ?? 'Confirm'}
        onClose={handleCancel}
        onConfirm={handleConfirm}
      />
    </ConfirmationContext.Provider>
  )
}

export const useConfirmation = () => {
  const context = React.useContext(ConfirmationContext)

  if (!context) {
    throw new Error('useConfirmation must be used within ConfirmationProvider')
  }

  return context.createConfirmation
}
